import {
  createFileRoute,
  Navigate,
  Outlet,
  useLocation,
  useMatches,
  useRouter,
  useRouterState,
  useNavigate,
  useSearch,
  type AnyRoute,
} from '@tanstack/react-router'
import { trpc } from '@/main'
import { useSessionUser, useSessionUserQuery } from '@/hooks/use-session'
import { Suspense, useEffect, type ReactNode } from 'react'
import { resolveRouteAccess, type RouteRedirect } from '@/route-access'
import { resolveEntryRedirect } from '@/entry-access'
import { postLoginHref, type PostLoginDestination } from 'shared/post-login'
import { UserMenu } from './-components/user-menu'
import { AppTopBar } from './-components/app-topbar'
import { AppBottomNav } from './-components/app-bottom-nav'
import { CompanyWelcomeDialog } from './-components/companies/welcome-dialog'
import { getChromeVariant } from './-components/app-layout-skeleton'
import { useAppStore } from '@/lib/app-store'
import { TransitionOutlet } from '@/components/custom/route-transition/surface/transition-outlet'
import { useTopLevelSections } from './-components/use-top-level-sections'
import { noop } from '@tanstack/react-query'

function useIsEmbedded(): boolean {
  const search = useSearch({ strict: false })
  const fromStore = useAppStore(
    (state) => state.store.sdk || !!state.store.clientId || !!state.store.app || !!state.store.userToken,
  )
  return !!search.token || !!search.clientId || fromStore
}

export const Route = createFileRoute('/_app')({
  context: () => ({
    myEmployeeProfileQueryOptions: trpc.employees.getMyEmployeeProfile.queryOptions(),
  }),
  loader: ({ context }) => {
    context.queryClient.query(context.myEmployeeProfileQueryOptions).catch(noop)
  },
  component: AppLayout,
  // A SHELL: it never pends and never suspends, so the route under it is what paints a skeleton — its one session
  // read (the access guard) sits BELOW a boundary whose fallback is that route's own pending. Its component stays
  // in the entry bundle (no 'component' grouping = not code-split; 4.4 KB gzip) so an entry from the landing has
  // no shell chunk to await.
  codeSplitGroupings: [['errorComponent'], ['notFoundComponent']],
})

function AppLayout() {
  const matches = useMatches()
  const routeId = matches[matches.length - 1]?.routeId ?? ''
  // Inside a route-transition live preview (shadow router) the app chrome — topbar, framed <main> — already
  // exists in the real tree this preview renders inside; redrawing it would nest a second shell in the pane.
  // Render just the matched route (the section, or a nested report tab, strips further down). The access guard
  // is skipped too: the preview only mounts destinations the live app already deemed reachable, and a
  // <Navigate> here would break the pinned preview. See route-transition/route-preview.tsx.
  const { previewFrom } = Route.useRouteContext()
  if (previewFrom !== undefined) return <Outlet />

  const variant = getChromeVariant(routeId)
  if (variant === 'simple')
    return (
      <Guarded>
        <SimpleShell />
      </Guarded>
    )
  if (variant !== 'full')
    return (
      <Guarded>
        <Outlet />
      </Guarded>
    )
  return <FullChrome />
}

/**
 * The matched route's OWN pending, rendered by the shell while the access guard below still waits for the session:
 * what the router would present for that lane — the OUTERMOST route under this layout that declares one, as the
 * router presents the first unresolved match, not the leaf (an index leaf declares none) — so the guard's wait and
 * the route's are one skeleton. Also what every wait inside the guard shows, so no lane of the shell ever spins.
 */
function MatchedRoutePending() {
  const router = useRouter()
  const routeIds = useRouterState({ select: (s) => s.matches.map((m) => m.routeId) })
  const routes = router.routesById as unknown as Record<string, AnyRoute | undefined>
  const Pending = routeIds
    .slice(routeIds.indexOf(Route.id) + 1)
    .map((id) => routes[id]?.options.pendingComponent)
    .find((component) => component !== undefined)
  return Pending ? (
    <div className="flex h-full flex-col">
      {/* oxlint-disable-next-line react/static-components -- `Pending` is a LOOKUP of a route's declared
          `pendingComponent`, not a component defined inline; its identity is stable for a given route. */}
      <Pending />
    </div>
  ) : null
}

// The session read BELOW its own boundary, never above the outlet: the shell renders on the first byte and the
// route's skeleton streams inside it, instead of the whole app waiting as the shell's fallback for as long as the
// session takes (measured on production as a blank, then a spinner, on every refresh).
function Guarded(props: { children: ReactNode }) {
  return (
    <Suspense fallback={<MatchedRoutePending />}>
      <AccessGuard>{props.children}</AccessGuard>
    </Suspense>
  )
}

function AccessGuard(props: { children: ReactNode }) {
  const matches = useMatches()
  const routeId = matches[matches.length - 1]?.routeId ?? ''
  // Single, central access guard for the whole private area (see route-access). Resolves the session user
  // (already loaded by the root) + the active route against one policy and redirects BEFORE the route's
  // auth/context-gated query mounts — so the gated query never fires, SSR never throws, and no route
  // component carries its own <Navigate>. getUser is read by the root above us, so this is a cache hit.
  const { data: user, isFetching: sessionRevalidating } = useSessionUserQuery()
  const location = useLocation()
  // `strict: false`: this guard sits above every route, so it reads whatever the matched one declared — `redirect`
  // is the login route's own search, and `useLocation().search` is not parsed against it up here.
  const entrySearch = useSearch({ strict: false }) as { redirect?: string }
  const redirect = resolveRouteAccess({ routeId, user, href: location.href })
  // The mirror of the policy above: /login and /register are for visitors who are not signed in yet, so a
  // finished user who lands on one goes where they would have gone had they just signed in (see entry-access).
  const entry = resolveEntryRedirect({ routeId, user, redirect: entrySearch.redirect })
  // Every case in that policy is decided off the user's flags, so none of them may be decided off a
  // REVALIDATING `getUser`: the post-write revalidation invalidates an active query without dropping its
  // data (lib/revalidate), so for one round trip `user` still describes who the viewer was BEFORE the
  // write. The write that signs someone up or claims an invitation is exactly that case — it lands them
  // here with a pre-write `null`, and this guard bounced them to /login with their real session already
  // in the cookie. Hold every redirect until the refetch settles, the same wait register/applicant makes
  // before its own. Not `user === undefined`: useSuspenseQuery never renders without data.
  if (redirect) return sessionRevalidating ? <MatchedRoutePending /> : <AccessRedirect redirect={redirect} />
  // Held for the same reason: a revalidating `getUser` still describes the viewer BEFORE the write, and the
  // write that finishes onboarding is exactly the one that lands them here.
  if (entry) return sessionRevalidating ? <MatchedRoutePending /> : <EntryRedirect destination={entry} />
  return props.children
}

/** Imperative, not `<Navigate>`: a post-login destination may be an opaque href, which that component cannot take.
 *  The href string is the effect's dependency, so a fresh object each render cannot re-fire it. */
function EntryRedirect({ destination }: { destination: PostLoginDestination }) {
  const navigate = useNavigate()
  const href = postLoginHref(destination)
  useEffect(() => {
    navigate({ href, replace: true })
  }, [navigate, href])
  return <MatchedRoutePending />
}

// Render the typed <Navigate> for a resolved access redirect. The discriminated union keeps each `to`
// a route-tree literal (so TanStack type-checks it), and only /login carries the return `redirect`.
function AccessRedirect({ redirect }: { redirect: RouteRedirect }) {
  if (redirect.to === '/login') return <Navigate to="/login" search={{ redirect: redirect.redirect }} replace />
  if (redirect.to === '/employees') return <Navigate to="/employees" replace />
  if (redirect.to === '/employees/$employeeId/report')
    return <Navigate to="/employees/$employeeId/report" params={{ employeeId: redirect.employeeId }} replace />
  if (redirect.to === '/report') return <Navigate to="/report" replace />
  return <Navigate to={redirect.to} replace />
}

function FullChrome() {
  const embedded = useIsEmbedded()
  if (embedded) {
    return (
      <div className="mx-2 flex h-dvh flex-col items-center md:mx-4" data-app-layout>
        <main className="max-w-9xl m-2 w-full flex-1 overflow-auto rounded-t-xl rounded-b-md border md:m-4 print:m-0 print:overflow-visible print:rounded-none print:border-0">
          <Guarded>
            <Outlet />
          </Guarded>
        </main>
      </div>
    )
  }

  // The chrome renders at once: the topbar and the bottom bar carry their own skeleton fallbacks, and the pane holds
  // the route's, so nothing here waits for the session.
  return (
    <div className="mx-2 flex h-dvh flex-col items-center md:mx-4" data-app-layout>
      <AppTopBar />
      <main className="max-w-9xl m-2 mt-0 w-full flex-1 overflow-auto rounded-t-xl rounded-b-md border md:m-4 md:mt-0 print:m-0 print:overflow-visible print:rounded-none print:border-0">
        <Guarded>
          <SectionPager />
        </Guarded>
      </main>
      {/* Mobile only: the section links live down here instead of in the topbar. In-flow sibling of <main>
          (the shell is h-dvh and never scrolls), so it's permanently on screen without overlaying content. */}
      <AppBottomNav />
      <Suspense fallback={null}>
        <CompanyWelcomeDialog />
      </Suspense>
    </div>
  )
}

// The app's ROOT scroll-snap surface: the viewer's top-level sections (Teams / Jobs / Offers / …) become
// swipeable full-width panes, and a section nav click glides straight there. LATERAL — the sections are
// PEERS, not levels of a hierarchy, so no depth parallax and no cast shadow. A single-section viewer gets a plain <Outlet/> (nothing to
// swipe). Only in the full (non-embedded) chrome; the embedded SDK shell keeps a plain outlet.
function SectionPager() {
  const sections = useTopLevelSections()
  if (sections.length <= 1) return <Outlet />
  // h-full (not flex-1): <main> is a block with a definite height, so the stage fills it and the sections'
  // own `h-full` cascades — the same contract the plain <Outlet/> had here.
  return <TransitionOutlet group={sections} className="h-full" />
}

function SimpleShell() {
  const user = useSessionUser()
  const embedded = useIsEmbedded()

  return (
    <>
      {user && !embedded && (
        <div className="flex justify-end px-4 pt-3 print:hidden">
          <UserMenu />
        </div>
      )}
      <Outlet />
    </>
  )
}
